// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import { removeProducts, getCart, type DecoratedProduct } from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Base } from '@gandi/ui';

import { normalizeMailboxProduct } from '../modules/utils';

import { MailboxRenewGroupItem } from './MailboxRenewGroupItem';
import ProductItemBase from './components/ProductItemBase';
import { TitleRow, TotalRow } from './components/Rows';

type GroupProps = {
  groupProducts: Array<DecoratedProduct>,
};

function GroupMailboxByType({ groupProducts }: GroupProps): React.MixedElement {
  const dispatch = useDispatch();
  const { __ } = useTranslator();

  const firstProduct = groupProducts[0];
  const { name, capacity } = normalizeMailboxProduct(firstProduct);

  const label = __('Mailbox %(name)s %(capacity)s', {
    name,
    capacity,
  });

  const totalPrice = groupProducts.reduce(
    (sum: number, product: DecoratedProduct): number =>
      sum + (product.total?.before_taxes ?? 0),
    0,
  );

  const handleRemoveGroup = (e: SyntheticEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    const ids = groupProducts.map((p: DecoratedProduct) => ({
      id: p.id,
    }));

    dispatch(removeProducts(ids))
      .then(() => dispatch(getCart()))
      .catch(() => {
        notifyDanger({
          message: __('An error has occurred. Please try again.'),
        });
      });
  };

  return (
    <ProductItemBase
      header={
        <TitleRow
          label={label}
          productType={firstProduct.productType}
          handleRemoveAction={handleRemoveGroup}
          warningContent={
            <Base tagName="p" margin="none">
              {__(
                'Warning: Not renewing these mailboxes will result in the permanent and total loss of all data after the expiration date.',
              )}
            </Base>
          }
        />
      }
      footer={<TotalRow hasTaxes={false} totalPrice={totalPrice} />}
    >
      {groupProducts.map((p: DecoratedProduct): React.MixedElement => (
        <MailboxRenewGroupItem key={p.id} product={p} />
      ))}
    </ProductItemBase>
  );
}

type Props = {
  groupedProducts: { [mailboxType: string]: Array<DecoratedProduct> },
};

export function MailboxRenewGroup({
  groupedProducts,
}: Props): React.MixedElement | null {
  const entries = Object.entries(groupedProducts);

  if (entries.length === 0) {
    return null;
  }

  return (
    <>
      {entries.map(([type, group]) => {
        const groupProducts = group as Array<DecoratedProduct>;

        return <GroupMailboxByType key={type} groupProducts={groupProducts} />;
      })}
    </>
  );
}
